import { LOADING_SIGNUP, AUTH, LOADING_LOGIN, AUTH_ERROR, LOGOUT, LOADING_APP, ERRORS } from './types';
import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { AppDispatch, AppThunk } from '../store';
import { server_url } from '../../feature-module/Utils/Helper';

interface ResponseData {
  success: boolean;
  user?: any;
  token?: string;
  classes?: string;
  current_class?: string;
  msg?: string;
  error?: any;
  school?: any[];
  academic_calendar?: any[];
  school_id: string;
  role?: string; // Add the role property
}

interface DispatchFunction {
  (action: { type: string; payload?: any }): void;
}
// Define the ThunkAction type for the login function
export function login(
  {
    username,
    password,
    school_id,
    history,
    type = "users",
  }: {
    username: string;
    password: string;
    school_id: string;
    history: any;
    type?: string;
  },
  success: (data: ResponseData) => void,
  error: (err: any) => void
): ThunkAction<void, {}, unknown, AnyAction> {
  return (dispatch: DispatchFunction) => {
    dispatch({ type: LOADING_LOGIN });

    fetch(`${server_url}/${type}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password, school_id }),
    })
      .then((raw) => raw.json())
      .then((data: ResponseData) => {
        dispatch({ type: LOADING_LOGIN });
        if (data.success) {
          const { token, school_id } = data;
          if (token) {
            localStorage.setItem("@@auth_token", token);
            localStorage.setItem("school_id", school_id);
          
          getUserProfile(token, history)
            .then((userData: any) => {
              if (data.success) {
                dispatch({ type: LOADING_APP });
                const {role } = userData;
                if (role) {
                  dispatch({
                    type: AUTH,
                    payload: {...data, school_id},
                  });
                  success({...data, school_id});
                }
              } else {
                localStorage.removeItem("@@auth_token");
                localStorage.removeItem("school_id");
                logout(history);
                error("Logged out.");
              }
            })
            .catch((err) => {
              logout(history);
              dispatch({
                type: ERRORS,
                payload: { msg: "Authentication failed", error:err },
              });
              error("Authentication failed.");
            });
          }
        } else {
          dispatch({ type: ERRORS, payload: data.msg });
          error(data.error);
        }
      })
      .catch((err) => {
        dispatch({ type: LOADING_LOGIN });
        console.error(err);
        
        error("Authentication failed.");
      });
  };
}

export function superadminlogin(
  {
    username,
    password,
    history,
  }: {
    username: string;
    password: string;
    history: any;
  },
  success: (data: ResponseData) => void,
  error: (err: any) => void
): ThunkAction<void, {}, unknown, AnyAction> {
  return (dispatch: DispatchFunction) => {
    dispatch({ type: LOADING_LOGIN });

    // Use the /superadmin-login endpoint
    fetch(`${server_url}/superadmin-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }), // Send username and password
    })
      .then((raw) => raw.json())
      .then((data: ResponseData) => {
        dispatch({ type: LOADING_LOGIN });

        if (data.success) {
          const { token } = data;

          if (token) {
            // Save the token in localStorage
            localStorage.setItem("@@auth_token", token);

            // Fetch the user profile using the token
            getUserProfile(token, history)
              .then((userData: any) => {
                if (userData.success) {
                  dispatch({ type: LOADING_APP });

                  const { role } = userData;

                  // Ensure the user is a Super Admin
                  if (role?.toLowerCase() === "superadmin") {
                    dispatch({
                      type: AUTH,
                      payload: { ...data, role: "superadmin" },
                    });
                    success(data); // Pass data to success callback
                  } else {
                    // If the user is not a Super Admin, log them out
                    localStorage.removeItem("@@auth_token");
                    logout(history);
                    error("You do not have permission to access this page.");
                  }
                } else {
                  // If user profile fetch fails, log the user out
                  localStorage.removeItem("@@auth_token");
                  logout(history);
                  error("Failed to fetch user profile.");
                }
              })
              .catch((err) => {
                // Handle errors during profile fetch
                logout(history);
                dispatch({
                  type: ERRORS,
                  payload: { msg: "Authentication failed", error: err },
                });
                error("Authentication failed.");
              });
          }
        } else {
          // If login fails, dispatch an error
          dispatch({ type: ERRORS, payload: data.msg });
          error(data.error || "Login failed.");
        }
      })
      .catch((err) => {
        // Handle network or other errors
        dispatch({ type: LOADING_LOGIN });
        console.error(err);
        error("Authentication failed. Please check your connection and try again.");
      });
  };
}

export const login_for_school = (
  {
    username,
    password,
    school_id,
    history,
    type = "users",
  }: {
    username: string;
    password: string;
    school_id: string; // Ensure school_id is always a string
    history: any;
    type?: string;
  },
  success: (data: any) => void,
  error: (err: any) => void
): AppThunk => {
  return (dispatch) => {
    dispatch({ type: LOADING_LOGIN });

    fetch(`${server_url}/${type}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password, school_id }),
    })
      .then((raw) => raw.json())
      .then((data: any) => {
        dispatch({ type: LOADING_LOGIN });
        if (data.success) {
          const { token } = data;
          if (token) {
            localStorage.setItem("@@auth_token", token);
            localStorage.setItem("school_id", school_id);

            getUserProfile(token, history)
              .then((userData: any) => {
                if (userData.success) {
                  dispatch({ type: LOADING_APP });
                  const { user, role } = userData;
                  if (role) {
                    dispatch({
                      type: AUTH,
                      payload: { ...data, school_id },
                    });
                    success(data);
                  }
                } else {
                  localStorage.removeItem("@@auth_token");
                  localStorage.removeItem("school_id");
                  logout(history);
                  error("Logged out.");
                }
              })
              .catch((err) => {
                logout(history);
                dispatch({
                  type: ERRORS,
                  payload: { msg: "Authentication failed", error: err },
                });
                error("Authentication failed.");
              });
          }
        } else {
          dispatch({ type: ERRORS, payload: data.msg });
          error(data.error);
        }
      })
      .catch((err) => {
        dispatch({ type: LOADING_LOGIN });
        console.error(err);
        
        error("Authentication failed.");
      });
  };
};

export function signup(
  objs: Record<string, any> = {},
  success: (data: ResponseData) => void = () => {},
  error: (err: any) => void = () => {}
) {
  return (dispatch: DispatchFunction) => {
    dispatch({ type: LOADING_SIGNUP });
    fetch(`${server_url}/sign_up`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(objs),
    })
      .then((raw) => raw.json())
      .then((datas: ResponseData) => {
        dispatch({ type: LOADING_SIGNUP });
        if (datas.success) {
          success(datas);
        } else {
          dispatch({
            type: AUTH_ERROR,
            payload: { msg: "Server error, try again." },
          });
          error(datas);
        }
      })
      .catch((err) => {
        dispatch({ type: LOADING_SIGNUP });
        console.log({ err });
        error(err);
      });
  };
}

export function expressSignup(
  objs: Record<string, any> = {},
  success: (data: ResponseData) => void = () => {},
  error: (err: any) => void = () => {}
) {
  return (dispatch: DispatchFunction) => {
    dispatch({ type: LOADING_SIGNUP });
    fetch(`${server_url}/sign_up`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(objs),
    })
      .then((raw) => raw.json())
      .then((datas: ResponseData) => {
        dispatch({ type: LOADING_SIGNUP });
        if (datas.success) {
          success(datas);
        } else {
          dispatch({
            type: AUTH_ERROR,
            payload: { msg: "Server error, try again." },
          });
          error(datas);
        }
      })
      .catch((err) => {
        dispatch({ type: LOADING_SIGNUP });
        console.log({ err });
        error(err);
      });
  };
}

export async function getUserProfile(
  _token: string,
  history: any
): Promise<any> {
  try {
    const response = await fetch(`${server_url}/verify-token`, {
      method: "GET",
      headers: {
        authorization: _token,
      },
    });
    return await response.json();
  } catch (error: any) {
    if (error.status === 401) {
      logout(history);
      window.location.reload();
    }
    return error;
  }
}

// export function init(
//   history: any,
//   success: () => void = () => {},
//   error: () => void = () => {}
// ) {
//     return (dispatch: AppDispatch) => {
//         const _token = localStorage.getItem('@@auth_token');
//         if (_token) {
//             getUserProfile(_token, history)
//                 .then((data: ResponseData) => {
//                     if (data.success) {
//                         const { user, classes,  } = data;
//                         if (user && user.role) {
//                             dispatch({
//                                 type: AUTH,
//                                 payload: data,
//                             });
//                             success();
//                         }
//                     } else {
//                         localStorage.removeItem('@@auth_token');
//                         logout(history);
//                         success();
//                     }
//                 })
//                 .catch((error) => {
//                     dispatch({
//                         type: ERRORS,
//                         payload: { msg: 'Authentication failed', error },
//                     });
//                     logout(history);
//                     error();
//                 });
//         } else {
//             logout(history)
//             dispatch({ type: ERRORS, payload: { msg: 'Authentication failed' } });
//             error();
//         }
//     };
// }

export function init(
  history: any,
  success: () => void = () => {},
  error: () => void = () => {}
) {
  return (dispatch: AppDispatch) => {
    const _token = localStorage.getItem("@@auth_token");
    const school_id = localStorage.getItem("school_id"); // Retrieve school_id from localStorage

    if (_token) {
      getUserProfile(_token, history)
        .then((data: ResponseData) => {
          if (data.success) {
            const { user, classes } = data;
            if (user && user.role) {
              dispatch({
                type: AUTH,
                payload: { ...data, school_id }, // Include school_id in the payload
              });
              success();
            }
          } else {
            localStorage.removeItem("@@auth_token");
            localStorage.removeItem("school_id"); // Remove school_id on failure
            logout(history);
            success();
          }
        })
        .catch((err) => {
          dispatch({
            type: ERRORS,
            payload: { msg: "Authentication failed", error: err },
          });
          logout(history);
          error();
        });
    } else {
      logout(history);
      dispatch({ type: ERRORS, payload: { msg: "Authentication failed" } });
      error();
    }
  };
}

// export function logout(history: any) {
//   return (dispatch: DispatchFunction) => {

//     localStorage.removeItem("@@auth_token");
//     if (
//       history.location.pathname !== "/sign-up" &&
//       history.location.pathname !== "/login"
//     ) {
//       history("/");
//     }
//     console.error("LOGGING OUT");

//     dispatch({ type: LOGOUT });
//   };
// }

export function logout(history: any) {
  return (dispatch: DispatchFunction) => {
    localStorage.removeItem("@@auth_token");
    localStorage.removeItem("school_id"); // Remove school_id on logout

    if (
      history.location.pathname !== "/sign-up" &&
      history.location.pathname !== "/login"
    ) {
      history("/");
    }
    console.error("LOGGING OUT");

    dispatch({ type: LOGOUT });
  };
}
